// (full file content with handleSaveTemplate added)
// VIDE CONSTITUICAO_REGRAS.SH sec.2.4 2.8 | Art.61.1 61.3 172 173 185 (Financas Pessoais PF)
// ============================================================================
// MÓDULO 12: PERSONAL FINANCE ERP (V12.0 - SERVICE LAYER / PAIR-AWARE)
// ============================================================================

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../shared/api/firebase/config";
import { FIREBASE_CONFIG } from "../../config/Config";
import { DEFAULT_CATEGORIES } from "./personal/domain/categories.js";
import { usePostings } from "./personal/hooks/usePostings.js";
import { usePairedView } from "./personal/hooks/usePairedView.js";
import { useReconciliation } from "./personal/hooks/useReconciliation.js";
import { useRectaBlingSnapshot } from "./useRectaBlingSnapshot.js";
import { PairRow } from "./personal/components/PairRow.jsx";
import { ReconciliationPanel } from "./personal/components/ReconciliationPanel.jsx";
import { PostingModal } from "./personal/components/PostingModal.jsx";
import { BatchImportPanel } from "./personal/components/BatchImportPanel.jsx";
import {
  DrillDownModal,
  CategoryManagerModal,
  InitialBalanceModal,
} from "./personal/components/FinanceModals.jsx";
import { DailyLiquidityPanel } from "./personal/components/DailyLiquidityPanel.jsx";
import { YearlyGrid } from "./personal/components/YearlyGrid.jsx";
import { DashboardCards } from "./personal/components/DashboardCards.jsx";
import { BlingPJPanel } from "./personal/components/BlingPJPanel.jsx";
import RecurrenceExpiryAlert from "./personal/components/RecurrenceExpiryAlert.jsx";
import { KpiSplitChips } from "./personal/components/KpiSplitChips.jsx";
import { SettlementModal } from "./personal/components/SettlementModal.jsx";
import { TrendsPanel } from "./personal/components/TrendsPanel.jsx";
import { useTrendsData } from "./personal/hooks/useTrendsData.js";
import { ConsolidationModal } from "./personal/components/ConsolidationModal.jsx";
import { OcrCaptureModal } from "./personal/components/OcrCaptureModal.jsx";

export { DEFAULT_CATEGORIES };

const formatNumber = (val) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(Number(val) || 0));

// ============================================================================
// Componente Principal
// ============================================================================
export const PersonalFinanceManager = ({
  extCurrentDate,
  onCurrentDateChange,
  extFilterMode,
  onFilterModeChange,
  openBalanceTrigger = 0,
  openCategoriesTrigger = 0,
  openNewEntryTrigger = 0,
} = {}) => {
  const APP_ID = FIREBASE_CONFIG?.projectId || "portalrda-f312d";
  const PATH_POSTINGS = [
    "artifacts",
    APP_ID,
    "public",
    "data",
    "personal_postings",
  ];
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  useEffect(() => {
    runDiagnostic();
  }, []);

  const runDiagnostic = useCallback(async () => {
    setDiagnosticLoading(true);
    setDiagnosticData(null);
    try {
      const ref = collection(db, ...PATH_POSTINGS);
      const snap = await getDocs(query(ref, orderBy("date", "asc")));
      const allDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const byMonth = {};
      allDocs.forEach((d) => {
        const month = (d.date || "").substring(0, 7);
        if (!month) return;
        if (!byMonth[month]) byMonth[month] = [];
        byMonth[month].push(d);
      });
      const pairsById = {};
      allDocs.forEach((d) => {
        if (!d.pairId) return;
        if (!pairsById[d.pairId]) pairsById[d.pairId] = [];
        pairsById[d.pairId].push(d);
      });
      const gaps = [];
      Object.entries(pairsById).forEach(([pairId, docs]) => {
        const nums = docs
          .map((p) => {
            const m = (p.parcela || "").match(/(\d+)\/(\d+)/);
            return m
              ? {
                  idx: parseInt(m[1]),
                  total: parseInt(m[2]),
                  date: p.date,
                  parcela: p.parcela,
                  desc: p.description,
                  status: p.status,
                }
              : null;
          })
          .filter(Boolean);
        if (nums.length > 0) {
          const total = nums[0].total;
          const indices = nums.map((n) => n.idx).sort((a, b) => a - b);
          const esperado = Array.from({ length: total }, (_, i) => i + 1);
          const faltando = esperado.filter((i) => !indices.includes(i));
          if (faltando.length > 0)
            gaps.push({
              pairId: pairId.substring(0, 8),
              total,
              faltando,
              parcelas: nums,
            });
        }
      });
      const mensais = allDocs.filter(
        (d) => d.recorrencia && d.recorrencia.toLowerCase() === "mensal",
      );
      const mensalByName = {};
      mensais.forEach((d) => {
        const base = (d.description || "").replace(/\(\d+\/\d+\)/g, "").trim();
        if (!mensalByName[base]) mensalByName[base] = [];
        mensalByName[base].push(d);
      });
      const onlyOneMonth = [];
      Object.entries(mensalByName).forEach(([name, docs]) => {
        const meses = [
          ...new Set(docs.map((d) => d.date?.substring(0, 7))),
        ].sort();
        if (meses.length === 1)
          onlyOneMonth.push({ name, mes: meses[0], docs });
      });
      const typos = allDocs.filter(
        (d) => d.recorrencia === "Unica" || d.recorrencia === "unica",
      );
      setDiagnosticData({
        total: allDocs.length,
        byMonth,
        gaps,
        onlyOneMonth,
        typos,
      });
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setDiagnosticLoading(false);
    }
  }, []);

  const [editingTx, setEditingTx] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [view, setView] = useState("POSTINGS");
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState("DATE");
  const [yearViewMode, setYearViewMode] = useState("ALL");
  const [drillDownData, setDrillDownData] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [settlementTarget, setSettlementTarget] = useState(null);
  const [showConsolidation, setShowConsolidation] = useState(false);
  const [showOcrCapture, setShowOcrCapture] = useState(false);
  const [ocrBatchRows, setOcrBatchRows] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const currentYear = currentDate.getFullYear();
  const monthLabel = currentDate.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });

  const {
    postings,
    templates,
    yearPostings,
    loading,
    refreshing,
    categories,
    sud,
    naturezas,
    initialBalance,
    loadYearlyData,
    savePosting,
    deletePosting,
    saveCategories,
    saveInitialBalance,
    addSubcategory,
    addGroup,
    addSudValue,
    addNatureza,
    markPaid,
    linkBlingId,
    createChildPosting,
    saveTemplate,
    saveTemplateLabel,
  } = usePostings({ currentDate, filterMode, yearViewMode, view });

  useEffect(() => {
    if (extCurrentDate instanceof Date) setCurrentDate(extCurrentDate);
  }, [extCurrentDate]);
  useEffect(() => {
    if (extFilterMode && extFilterMode !== filterMode)
      setFilterMode(extFilterMode);
  }, [extFilterMode]);
  useEffect(() => {
    if (openBalanceTrigger > 0) setShowBalanceModal(true);
  }, [openBalanceTrigger]);
  useEffect(() => {
    if (openCategoriesTrigger > 0) setShowCategoryModal(true);
  }, [openCategoriesTrigger]);
  useEffect(() => {
    if (openNewEntryTrigger > 0) {
      setEditingTx(null);
      setIsDuplicating(false);
      setShowModal(true);
    }
  }, [openNewEntryTrigger]);

  const filteredPostings = useMemo(() => {
    const activePostings = postings.filter((p) => p.status !== "CANCELLED");
    const sortPostings = (items) =>
      [...items].sort((a, b) => {
        const dateDiff =
          new Date(a.date + "T12:00:00") - new Date(b.date + "T12:00:00");
        if (dateDiff !== 0) return dateDiff;
        const pairA = a.pairedSortKey || a.pairId || a.id || "";
        const pairB = b.pairedSortKey || b.pairId || b.id || "";
        if (pairA !== pairB) return String(pairA).localeCompare(String(pairB));
        const orderA = Number.isFinite(Number(a.sortOrder))
          ? Number(a.sortOrder)
          : a.group === "RECEITAS"
            ? 0
            : 1;
        const orderB = Number.isFinite(Number(b.sortOrder))
          ? Number(b.sortOrder)
          : b.group === "RECEITAS"
            ? 0
            : 1;
        if (orderA !== orderB) return orderA - orderB;
        return String(a.description || "").localeCompare(
          String(b.description || ""),
        );
      });
    if (!searchTerm) return sortPostings(activePostings);
    const lowerTerm = searchTerm.toLowerCase();
    return sortPostings(
      activePostings.filter(
        (p) =>
          p.description?.toLowerCase().includes(lowerTerm) ||
          p.category?.toLowerCase().includes(lowerTerm) ||
          p.account?.toLowerCase().includes(lowerTerm) ||
          p.vinculo?.toLowerCase().includes(lowerTerm),
      ),
    );
  }, [postings, searchTerm]);

  const pairedView = usePairedView(filteredPostings);
  const yearMonth = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  const {
    data: blingSnapshot,
    loading: blingLoading,
    error: blingError,
    refreshFromServer: refreshBling,
  } = useRectaBlingSnapshot();
  const {
    result: reconcileResult,
    health: reconcileHealth,
    run: runReconcile,
    hasRun: reconcileHasRun,
  } = useReconciliation(postings, blingSnapshot, yearMonth);

  const summary = useMemo(() => {
    const byGroup = {};
    Object.keys(categories || {}).forEach((k) => {
      byGroup[k] = { total: 0, paid: 0, pending: 0, forecast: 0 };
    });
    filteredPostings.forEach((t) => {
      const val = Number(t.value);
      if (!byGroup[t.group])
        byGroup[t.group] = { total: 0, paid: 0, pending: 0, forecast: 0 };
      if (t.status === "FORECAST") byGroup[t.group].forecast += val;
      else {
        byGroup[t.group].total += val;
        if (t.status === "PAID") byGroup[t.group].paid += val;
        else byGroup[t.group].pending += val;
      }
    });
    // NOTA: Templates ativos NÃO entram no byGroup — padrão ouro YNAB/PocketGuard
    // (apenas transações reais nos cards de categoria. Templates aparecem na tabela como previstos.)
    // EXPENSE real = soma apenas dos lançamentos filtrados (sem templates)
    const realExpense = filteredPostings
      .filter((p) => p.group !== "RECEITAS")
      .reduce((acc, p) => acc + Number(p.value), 0);
    const income = byGroup.RECEITAS?.total || 0;
    let expense = 0;
    Object.entries(byGroup).forEach(([k, v]) => {
      if (k !== "RECEITAS") expense += v.total;
    });
    const realPending = filteredPostings
      .filter((p) => p.group !== "RECEITAS" && p.status !== "PAID")
      .reduce((acc, p) => acc + Number(p.value), 0);
    const incomeTotal = byGroup.RECEITAS?.total || 0;
    const savingsRate =
      incomeTotal > 0
        ? Math.round(((incomeTotal - realExpense) / incomeTotal) * 100)
        : 0;
    return {
      income,
      expense,
      realExpense,
      realPending,
      savingsRate,
      pets: byGroup.PETS?.total || 0,
      future: byGroup.FUTURO?.total || 0,
      balance: income - realExpense,
      byGroup,
    };
  }, [filteredPostings, templates, searchTerm, postings, categories]);

  const kpiTotals = useMemo(() => {
    let paid = 0,
      pending = 0,
      forecast = 0;
    filteredPostings.forEach((p) => {
      if (p.group === "RECEITAS") return;
      const v = Number(p.value);
      if (p.status === "PAID") paid += v;
      else if (p.status === "FORECAST") forecast += v;
      else pending += v;
    });
    return { paid, pending, forecast };
  }, [filteredPostings]);

  const statusFilteredView = useMemo(() => {
    if (!statusFilter) return pairedView;
    return pairedView.filter((item) => {
      if (item.type === "pair")
        return (
          item.income?.status === statusFilter ||
          item.expense?.status === statusFilter
        );
      return item.posting?.status === statusFilter;
    });
  }, [pairedView, statusFilter]);

  // Ordenação clicável das colunas do Extrato Mensal (pedido do titular,
  // 18/09/2026). Um par (despesa+receita-espelho) ordena pelo lado da
  // despesa, pra continuar mostrando as duas linhas juntas.
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "asc" });
  const getSortValue = (item, key) => {
    const t = item.type === "pair" ? item.expense || item.income : item.posting;
    if (!t) return "";
    switch (key) {
      case "date":
        return t.date || "";
      case "tipo":
        return t.parcela ? "parcelado" : (t.recorrencia || "").toLowerCase();
      case "descricao":
        return (t.description || "").toLowerCase();
      case "conta":
        return (t.account || "").toLowerCase();
      case "resp":
        return (t.vinculo || "").toLowerCase();
      case "categoria":
        return (t.group || "").toLowerCase();
      case "status":
        return (t.status || "").toLowerCase();
      case "valor":
        return Number(t.value) || 0;
      default:
        return "";
    }
  };
  const handleSortClick = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };
  const sortedView = useMemo(() => {
    const arr = [...statusFilteredView];
    arr.sort((a, b) => {
      const va = getSortValue(a, sortConfig.key);
      const vb = getSortValue(b, sortConfig.key);
      if (va < vb) return sortConfig.direction === "asc" ? -1 : 1;
      if (va > vb) return sortConfig.direction === "asc" ? 1 : -1;
      // Desempate fixo (previsibilidade): mesma data/valor sempre na mesma
      // ordem, por descrição, em todo mês. Sem isso a ordem dependia do id
      // aleatório do documento e mudava de um mês para o outro.
      return getSortValue(a, "descricao").localeCompare(
        getSortValue(b, "descricao"),
        "pt-BR",
      );
    });
    return arr;
  }, [statusFilteredView, sortConfig]);

  // Mantém o alinhamento vertical ao trocar de mês (pedido do titular,
  // 18/09/2026). Meses têm tamanhos muito diferentes (ex: 2 linhas em agosto,
  // 59 em maio): ao entrar num mês curto a página encolhia, o navegador
  // forçava a rolagem pra cima e o mês seguinte já abria deslocado. Antes de
  // trocar, garante que a página tenha altura pra manter a rolagem atual
  // (rolagem + altura visível); o espaço extra fica só abaixo das linhas.
  const rootRef = useRef(null);
  const [minPageHeight, setMinPageHeight] = useState(0);
  const holdScrollPosition = () => {
    let el = rootRef.current?.parentElement;
    while (el && !/(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
      el = el.parentElement;
    }
    if (el) setMinPageHeight(el.scrollTop + el.clientHeight);
  };

  const changeMonth = (offset) => {
    holdScrollPosition();
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + offset);
    setCurrentDate(newDate);
    if (typeof onCurrentDateChange === "function") onCurrentDateChange(newDate);
  };

  // `opts.silent` existe para o lançamento em lote (BatchImportPanel): lá cada
  // linha já tem seu próprio indicador visual de erro (badge "❌ Erro"), então
  // um alert() bloqueante por linha seria péssimo em lote de várias linhas —
  // o chamador silencioso precisa, em troca, do erro relançado (throw) pra
  // marcar a linha como falha em vez de "concluído" por engano (achado real,
  // 17/09/2026: a receita-espelho de uma retirada PJ falhava e o lote
  // reportava sucesso mesmo assim, porque o erro nunca saía daqui).
  const handleSave = async (formData, mode, opts = {}) => {
    try {
      await savePosting(formData, mode);
      setShowModal(false);
      // Achado real 18/09/2026: o campo "Repetições" não gerava nada além
      // do mês da criação. Corrigido — agora gera de verdade os N meses
      // pedidos, no intervalo escolhido (Mensal/Bimestral/etc).
      if (!opts.silent && mode === "RECURRING" && !formData.id) {
        const n = parseInt(formData.repeatCount) || 1;
        alert(
          `"${formData.description}" criado em ${n} lançamento${n > 1 ? "s" : ""} (${formData.recorrencia || "Mensal"}), já visíve${n > 1 ? "is" : "l"} no Extrato Mensal de cada mês correspondente.`,
        );
      }
    } catch (e) {
      if (!opts.silent) alert("Erro: " + e.message);
      throw e;
    }
  };

  const handleSaveV2 = async (formData, opts = {}) => {
    const mode = formData.repeatMode || "SINGLE";
    // Diálogo "alterar TODOS x APENAS este" só faz sentido pra PARCELADO —
    // ali existem, de verdade, vários documentos irmãos (parcela 1/3, 2/3...)
    // pra escolher entre editar todos ou só um. "Recorrente" NÃO tem essa
    // relação: desde a correção de 18/09/2026, cada mês é um lançamento
    // independente, e o modelo de recorrência é um documento separado sem
    // vínculo 1:1 com o lançamento. Achado real: editar um lançamento
    // "Recorrente" caía nesse diálogo por engano e, ao clicar OK, criava um
    // NOVO template em vez de atualizar o lançamento — a edição "sumia".
    const isEditingSeries = mode === "INSTALLMENT" && !!editingTx?.id;
    if (!isEditingSeries) {
      await handleSave(formData, mode, opts);
      return;
    }
    const scope = window.confirm(
      `"${formData.description}"\n\nClique OK para alterar TODOS. Cancelar para APENAS este.`,
    );
    try {
      if (scope) {
        await savePosting(formData, mode);
      } else {
        await savePosting({ ...formData, forceSingle: true }, "SINGLE");
        try {
          await createChildPosting(formData, editingTx.id);
        } catch (e) {
          console.warn(e.message);
        }
      }
      setShowModal(false);
    } catch (e) {
      if (!opts.silent) alert("Erro: " + e.message);
      throw e;
    }
  };

  const handleSaveTemplate = async (tplData) => {
    try {
      await saveTemplate(tplData);
      alert(`Template "${tplData.templateId}" salvo com sucesso!`);
    } catch (e) {
      alert("Erro: " + e.message);
    }
  };

  const handleDelete = async (id, isTemplate = false) => {
    if (!confirm("Excluir?")) return;
    try {
      await deletePosting(id, isTemplate);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (tx) => {
    setEditingTx(tx);
    setIsDuplicating(false);
    setShowModal(true);
  };

  // Duplicar (pedido do titular, 18/09/2026): abre "Novo Lançamento" já
  // preenchido com os mesmos dados — útil pra casos como "mais um
  // streaming parecido com o Netflix, só muda o nome". Não reaproveita o
  // id (é uma criação nova de verdade) nem o status de pagamento (a cópia
  // nasce pendente, não "já paga" só porque o original estava).
  const handleDuplicate = (tx) => {
    setEditingTx({
      ...tx,
      id: undefined,
      isPaid: false,
      status: "PENDING",
      settlementDate: "",
      pairId: undefined,
      pairedSortKey: undefined,
      sortOrder: undefined,
    });
    setIsDuplicating(true);
    setShowModal(true);
  };

  const handleSaveCategories = async (newCats) => {
    try {
      await saveCategories(newCats);
      alert("OK!");
    } catch (e) {
      alert("Erro: " + e.message);
    }
  };
  const handleSaveInitialBalance = async (newBalance) => {
    try {
      await saveInitialBalance(newBalance);
      setShowBalanceModal(false);
    } catch (e) {
      alert("Erro: " + e.message);
    }
  };
  const handleVincular = async (divergence) => {
    const blingId = window.prompt(
      `Vincular ao Bling\n\nTipo: ${divergence.type}\nDetalhe: ${divergence.detail}`,
      divergence.bling?.blingId || "",
    );
    if (!blingId) return;
    try {
      const postingId = divergence.firestore?.id;
      if (!postingId) {
        alert("Sem ID");
        return;
      }
      await linkBlingId(postingId, blingId);
      alert(`✅ Vinculado.`);
    } catch (e) {
      alert("Erro: " + e.message);
    }
  };

  const handleCellClick = (group, monthIndex) => {
    const items = [];
    let total = 0;
    yearPostings.forEach((p) => {
      const pDate = new Date(p.date + "T12:00:00");
      if (p.group === group && pDate.getMonth() === monthIndex) {
        items.push({
          description: p.description,
          value: Number(p.value),
          category: p.category,
          status: p.status,
          account: p.account,
          isProjection: false,
        });
        total += Number(p.value);
      }
    });
    if (yearViewMode === "ALL") {
      const currentMonthIdx = new Date().getMonth();
      if (monthIndex >= currentMonthIdx) {
        templates.forEach((t) => {
          if (t.active && t.group === group) {
            const exists = yearPostings.some(
              (p) =>
                p.templateId === t.id &&
                new Date(p.date + "T12:00:00").getMonth() === monthIndex,
            );
            if (!exists) {
              items.push({
                description: t.description,
                value: Number(t.amount),
                category: t.category,
                status: "PROJECTION",
                account: t.account,
                isProjection: true,
              });
              total += Number(t.amount);
            }
          }
        });
      }
    }
    const monthName = new Date(0, monthIndex).toLocaleString("pt-BR", {
      month: "long",
    });
    setDrillDownData({
      title: `${group} - ${monthName}/${currentYear}`,
      monthLabel: monthName.toUpperCase(),
      items: items.sort((a, b) => b.value - a.value),
      total,
    });
  };

  const trendsData = useTrendsData(yearPostings, templates, categories);

  const dashboardCards = useMemo(() => {
    const fixedOrder = [
      "RECEITAS",
      "__EXPENSES__",
      "__BALANCE__",
      "__PENDING__",
      "SAUDE",
      "ESTILO",
      "PETS",
      "ALIMENTACAO",
      "FUTURO",
      "MORADIA",
      "TRANSPORTE",
      "ESSENCIAIS",
    ];
    const syntheticCards = {
      __EXPENSES__: {
        label: "💸 TOTAL DE SAÍDAS",
        color: "text-red-700 bg-red-50 border-red-200",
      },
      __BALANCE__: {
        label: "⚖️ SALDO DO PERÍODO",
        color:
          summary.balance < 0
            ? "text-red-700 bg-red-50 border-red-200"
            : "text-emerald-700 bg-emerald-50 border-emerald-200",
      },
      __PENDING__: {
        label: "📌 PREVISTO EM ABERTO",
        color: "text-amber-700 bg-amber-50 border-amber-200",
      },
    };
    const merged = { ...syntheticCards, ...(categories || {}) };
    const ordered = fixedOrder
      .filter((key) => merged[key])
      .map((key) => [key, merged[key]]);
    const remaining = Object.entries(merged).filter(
      ([key]) => !fixedOrder.includes(key),
    );
    return [...ordered, ...remaining.sort(([a], [b]) => a.localeCompare(b))];
  }, [categories, summary.balance]);

  if (loading && view !== "YEARLY")
    return (
      <div className="flex justify-center p-12">
        <i className="fas fa-spinner fa-spin text-3xl text-purple-600"></i>
      </div>
    );

  return (
    <div
      ref={rootRef}
      style={minPageHeight ? { minHeight: minPageHeight } : undefined}
      className="space-y-4 pb-10"
    >
      {/* Barra do mês — fixa desde o primeiro pixel (pedido do titular,
          18/09/2026): trocar de mês sem rolar de volta ao início. Precisa ser
          o PRIMEIRO elemento e usar -mt-8/-top-8 porque o painel de rolagem
          (AdminDashboard) tem p-8 e o sticky respeita esse padding: assim a
          barra já nasce colada no topo, na mesma posição em que fica ao rolar,
          e nunca se desloca. h-14 = 56px; o cabeçalho das colunas usa top-6
          (= -32px + 56px) para grudar logo abaixo dela. */}
      <div className="sticky -top-8 -mt-8 z-30 h-14 flex items-center justify-between bg-white border border-[#d1d5db] px-4 shadow-sm">
        <button
          onClick={() => changeMonth(-1)}
          className="px-3 py-1 text-sm font-semibold text-[#20243B] hover:bg-gray-100 border border-gray-200 rounded flex items-center gap-1"
        >
          <i className="fas fa-chevron-left text-[10px]"></i> Anterior
        </button>
        <div className="text-center">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Mês
          </p>
          <h3 className="text-lg font-bold text-gray-800">{monthLabel}</h3>
        </div>
        <button
          onClick={() => changeMonth(1)}
          className="px-3 py-1 text-sm font-semibold text-[#20243B] hover:bg-gray-100 border border-gray-200 rounded flex items-center gap-1"
        >
          Próximo <i className="fas fa-chevron-right text-[10px]"></i>
        </button>
      </div>

      {/* Diagnóstico */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (diagnosticData) setShowDiagnostic(!showDiagnostic);
            else runDiagnostic();
          }}
          disabled={diagnosticLoading}
          className="px-2 py-0.5 text-[9px] font-semibold text-[#7c3aed] hover:bg-[#f5f3ff] border border-[#ddd6fe] flex items-center gap-1"
        >
          <i className="fas fa-stethoscope text-[8px]"></i>
          {diagnosticLoading
            ? "Escaneando..."
            : diagnosticData
              ? showDiagnostic
                ? "Ocultar"
                : `📊 ${diagnosticData.total}`
              : "Diagnóstico"}
        </button>
      </div>
      {diagnosticData && showDiagnostic && (
        <div className="bg-white border border-[#d1d5db] p-4 text-[11px] font-mono max-h-80 overflow-y-auto">
          <div className="font-bold text-[#7c3aed] mb-2">
            📋 DIAGNÓSTICO — {diagnosticData.total} lançamentos
            <button
              onClick={() => setShowDiagnostic(false)}
              className="ml-2 text-gray-400 hover:text-red-500"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="mb-2">
            <strong>Por mês:</strong>{" "}
            {Object.entries(diagnosticData.byMonth)
              .sort()
              .map(([m, docs]) => (
                <span key={m} className="ml-2">
                  {m}: {docs.length}
                </span>
              ))}
          </div>
          {diagnosticData.gaps.length > 0 && (
            <div className="mb-2 text-red-600">
              <strong>⚠️ Gaps:</strong>{" "}
              {diagnosticData.gaps.map((g, i) => (
                <div key={i}>
                  Parcela {g.pairId}: falta(m) {g.faltando.join(", ")}
                </div>
              ))}
            </div>
          )}
          {diagnosticData.onlyOneMonth.length > 0 && (
            <div className="mb-2 text-amber-600">
              <strong>⚠️ 1 mês só:</strong>{" "}
              {diagnosticData.onlyOneMonth.map((item, i) => (
                <div key={i}>
                  "{item.name}" → {item.mes}
                </div>
              ))}
            </div>
          )}
          {diagnosticData.typos.length > 0 && (
            <div className="mb-2 text-orange-600">
              <strong>⚠️ "Unica":</strong>{" "}
              {diagnosticData.typos.map((t, i) => (
                <div key={i}>
                  {t.date} — {t.description}
                </div>
              ))}
            </div>
          )}
          {!diagnosticData.gaps.length &&
            !diagnosticData.onlyOneMonth.length &&
            !diagnosticData.typos.length && (
              <div className="text-emerald-600 font-bold">✅ OK</div>
            )}
        </div>
      )}

      <DashboardCards dashboardCards={dashboardCards} summary={summary} />
      <KpiSplitChips
        summary={summary}
        health={reconcileHealth}
        statusFilter={statusFilter}
        onFilterChange={setStatusFilter}
        totalPaid={kpiTotals.paid}
        totalPending={kpiTotals.pending}
        totalForecast={kpiTotals.forecast}
        countAll={filteredPostings.length}
      />
      <RecurrenceExpiryAlert postings={postings} templates={templates} />

      {/* overflow-clip (não hidden): hidden cria um contêiner de rolagem e
          impede o position:sticky do cabeçalho da tabela de funcionar. */}
      <div className="bg-white border border-[#d1d5db] overflow-clip">
        <div className="flex items-center bg-[#f3f4f6] border-b border-[#d1d5db] px-0">
          <div className="flex items-stretch">
            {[
              { id: "POSTINGS", label: "Extrato Mensal", icon: "fas fa-list" },
              {
                id: "YEARLY",
                label: "Visão Anual",
                icon: "fas fa-calendar-alt",
                onClick: () => {
                  setView("YEARLY");
                  loadYearlyData();
                },
              },
              { id: "BLING_PJ", label: "Bling PJ", icon: "fas fa-building" },
              {
                id: "RECONCILE",
                label: "Conciliação",
                icon: "fas fa-balance-scale",
              },
              { id: "BATCH", label: "Importar Lote", icon: "fas fa-table" },
              {
                id: "TRENDS",
                label: "📈 Tendências",
                icon: "fas fa-chart-line",
                onClick: () => {
                  setView("TRENDS");
                  loadYearlyData();
                },
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={tab.onClick || (() => setView(tab.id))}
                className={`px-4 py-1.5 text-[10px] font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${view === tab.id ? "bg-white text-[#20243B] border-[#20243B]" : "text-[#6b7280] border-transparent hover:bg-[#e5e7eb]"}`}
              >
                <i className={`${tab.icon} text-[9px]`}></i> {tab.label}
              </button>
            ))}
          </div>
          <div className="flex-1"></div>
          <div className="flex items-center gap-2 pr-2">
            <button
              onClick={() => {
                setEditingTx(null);
                setIsDuplicating(false);
                setShowModal(true);
              }}
              className="px-2 py-0.5 text-[9px] font-semibold text-white bg-[#20243B] hover:bg-[#1a1e30] flex items-center gap-1"
            >
              <i className="fas fa-plus text-[8px]"></i> Novo
            </button>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="px-2 py-0.5 text-[9px] font-semibold text-[#20243B] bg-white border border-[#d1d5db] hover:bg-[#f3f4f6] flex items-center gap-1"
              title="Gerenciar grupos, subcategorias e metas"
            >
              <i className="fas fa-tags text-[8px]"></i> Categorias
            </button>
            {view === "POSTINGS" && (
              <div className="relative w-52">
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="w-full pl-6 pr-6 py-0.5 text-[10px] border border-[#c8c8c8] bg-white focus:border-[#20243B] outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className="fas fa-search absolute left-2 top-[5px] text-[#9ca3af] text-[9px]"></i>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-1.5 top-[3px] text-[#9ca3af] hover:text-[#dc2626]"
                  >
                    <i className="fas fa-times text-[9px]"></i>
                  </button>
                )}
              </div>
            )}
            {searchTerm && view === "POSTINGS" && (
              <span className="text-[8px] font-bold text-[#20243B] bg-[#C5A059]/20 px-1.5 py-0.5">
                FILTRO
              </span>
            )}
            <button
              onClick={() => setShowOcrCapture(true)}
              className="px-2 py-0.5 text-[9px] font-semibold text-[#20243B] hover:bg-[#f3f4f6] border border-[#d1d5db] flex items-center gap-1"
            >
              <i className="fas fa-camera text-[8px]"></i>OCR
            </button>
            <button
              onClick={() => setShowConsolidation(true)}
              className="px-2 py-0.5 text-[9px] font-semibold text-[#059669] hover:bg-[#ecfdf5] border border-[#a7f3d0] flex items-center gap-1"
            >
              <i className="fas fa-check-double text-[8px]"></i>Consolidar
            </button>
            <button
              onClick={() => exportToCSV(filteredPostings)}
              className="px-2 py-0.5 text-[9px] font-semibold text-[#059669] hover:bg-[#ecfdf5] border border-[#a7f3d0] flex items-center gap-1"
            >
              <i className="fas fa-file-excel text-[8px]"></i>CSV
            </button>
          </div>
        </div>

        {view === "YEARLY" && (
          <YearlyGrid
            yearPostings={yearPostings}
            templates={templates}
            yearViewMode={yearViewMode}
            setYearViewMode={setYearViewMode}
            onCellClick={handleCellClick}
          />
        )}

        {view === "POSTINGS" && (
          <div
            className={`overflow-x-clip transition-opacity ${refreshing ? "opacity-50" : ""}`}
            aria-busy={refreshing}
          >
            <table className="w-full text-left text-[11px] border-collapse">
              <thead className="bg-[#f0f0f0] border-b border-[#c8c8c8] sticky top-6 z-20">
                <tr>
                  {[
                    ["date", "DATA", "w-[80px]", "left"],
                    ["tipo", "TIPO", "w-[90px]", "left"],
                    ["descricao", "DESCRIÇÃO", "", "left"],
                    ["conta", "CONTA", "w-[100px]", "left"],
                    ["resp", "RESP.", "w-[80px]", "left"],
                    ["categoria", "CATEGORIA", "w-[110px]", "left"],
                    ["status", "STATUS", "w-[80px]", "left"],
                    ["valor", "VALOR (R$)", "w-[80px]", "right"],
                  ].map(([key, label, widthClass, align]) => (
                    <th
                      key={key}
                      className={`px-2 py-1 text-[10px] font-semibold text-[#374151] ${widthClass} ${align === "right" ? "text-right" : "text-left"}`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSortClick(key)}
                        className={`inline-flex items-center gap-1 hover:text-[#20243B] ${align === "right" ? "flex-row-reverse" : ""}`}
                        title={`Ordenar por ${label}`}
                      >
                        {label}
                        <i
                          className={`fas ${
                            sortConfig.key === key
                              ? sortConfig.direction === "asc"
                                ? "fa-sort-up"
                                : "fa-sort-down"
                              : "fa-sort"
                          } text-[9px] ${sortConfig.key === key ? "text-[#20243B]" : "text-[#9ca3af]"}`}
                        ></i>
                      </button>
                    </th>
                  ))}
                  <th className="px-2 py-1 text-[10px] font-semibold text-[#374151] w-[60px]">
                    AÇÕES
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedView.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      className="p-8 text-center text-gray-400 text-[12px]"
                    >
                      Nenhum lançamento.
                    </td>
                  </tr>
                )}
                {sortedView.map((item) => (
                  <PairRow
                    key={
                      item.type === "pair"
                        ? `pair-${item.pairId}`
                        : `single-${item.posting.id}`
                    }
                    item={item}
                    categories={categories}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onDelete={(id) => handleDelete(id)}
                    onSettle={(t) => {
                      const pItem = pairedView.find(
                        (pv) =>
                          pv.type === "pair" &&
                          (pv.income?.id === t.id || pv.expense?.id === t.id),
                      );
                      setSettlementTarget(
                        pItem
                          ? {
                              pair: {
                                income: pItem.income,
                                expense: pItem.expense,
                              },
                            }
                          : { posting: t },
                      );
                    }}
                  />
                ))}
              </tbody>
              {filteredPostings.length > 0 &&
                (() => {
                  const totalNet = filteredPostings.reduce(
                    (s, t) =>
                      s +
                      (t.group === "RECEITAS"
                        ? Number(t.value)
                        : -Number(t.value)),
                    0,
                  );
                  const isNeg = totalNet < 0;
                  return (
                    <tfoot
                      className={`border-t-2 font-bold ${isNeg ? "bg-[#fef2f2] border-[#fecaca]" : "bg-[#ecfdf5] border-[#a7f3d0]"}`}
                    >
                      <tr>
                        <td
                          colSpan="7"
                          className={`px-2 py-1.5 text-[10px] uppercase tracking-wide text-right ${isNeg ? "text-[#dc2626]" : "text-[#059669]"}`}
                        >
                          Saldo ({filteredPostings.length}{" "}
                          {filteredPostings.length === 1
                            ? "lançamento"
                            : "lançamentos"}
                          )
                        </td>
                        <td
                          className={`px-2 py-1.5 text-right font-mono tabular-nums text-[13px] ${isNeg ? "text-[#dc2626]" : "text-[#059669]"}`}
                        >
                          {isNeg
                            ? `\u2212${formatNumber(totalNet)}`
                            : formatNumber(totalNet)}
                        </td>
                        <td className="px-2 py-1.5"></td>
                      </tr>
                    </tfoot>
                  );
                })()}
            </table>
          </div>
        )}

        {view === "BATCH" && (
          <div className="p-4">
            <BatchImportPanel
              externalRows={ocrBatchRows}
              onSaveBatch={(formData) =>
                handleSaveV2(formData, { silent: true })
              }
              onSaveTemplateLabel={saveTemplateLabel}
              categories={categories}
              sud={sud}
              onAddSudValue={addSudValue}
            />
          </div>
        )}
        {view === "BLING_PJ" && (
          <BlingPJPanel
            blingSnapshot={blingSnapshot}
            blingLoading={blingLoading}
            blingError={blingError}
            onRefreshBling={refreshBling}
          />
        )}
        {view === "RECONCILE" && (
          <ReconciliationPanel
            result={reconcileResult}
            health={reconcileHealth}
            hasRun={reconcileHasRun}
            onRun={runReconcile}
            blingLoading={blingLoading}
            blingError={blingError}
            onRefreshBling={refreshBling}
            onVincular={handleVincular}
          />
        )}
        {view === "TRENDS" && (
          <TrendsPanel trendsData={trendsData} categories={categories} />
        )}
      </div>

      {showModal && (
        <PostingModal
          onClose={() => setShowModal(false)}
          onSave={handleSaveV2}
          onSaveTemplate={handleSaveTemplate}
          categories={categories}
          sud={sud}
          naturezas={naturezas}
          onAddSubcategory={addSubcategory}
          onAddGroup={addGroup}
          onAddSudValue={addSudValue}
          onAddNatureza={addNatureza}
          history={postings}
          initialData={editingTx}
          forceNew={isDuplicating}
        />
      )}
      {showCategoryModal && (
        <CategoryManagerModal
          onClose={() => setShowCategoryModal(false)}
          categories={categories}
          onSaveCategories={handleSaveCategories}
        />
      )}
      {showBalanceModal && (
        <InitialBalanceModal
          onClose={() => setShowBalanceModal(false)}
          onSave={handleSaveInitialBalance}
          currentValue={initialBalance}
        />
      )}
      {drillDownData && (
        <DrillDownModal
          onClose={() => setDrillDownData(null)}
          data={drillDownData}
          title={drillDownData.title}
          monthLabel={drillDownData.monthLabel}
        />
      )}
      {showOcrCapture && (
        <OcrCaptureModal
          onClose={() => setShowOcrCapture(false)}
          onRowsExtracted={(rows) => {
            setOcrBatchRows(rows);
            setView("BATCH");
          }}
        />
      )}
      {showConsolidation && (
        <ConsolidationModal
          postings={postings}
          onClose={() => setShowConsolidation(false)}
          onConsolidate={async (items, settlementDate) => {
            for (const p of items) {
              await markPaid({ id: p.id, fonte: "firestore" }, settlementDate);
            }
            setShowConsolidation(false);
          }}
        />
      )}
      {settlementTarget && (
        <SettlementModal
          pair={settlementTarget.pair || null}
          posting={settlementTarget.posting || null}
          onClose={() => setSettlementTarget(null)}
          onConfirm={async ({ settlementDate, generateCounterpart }) => {
            try {
              if (settlementTarget.pair) {
                const pairPosting =
                  settlementTarget.pair.income || settlementTarget.pair.expense;
                await markPaid(
                  { id: pairPosting.id, fonte: "firestore" },
                  settlementDate,
                );
              } else if (settlementTarget.posting) {
                const posting = settlementTarget.posting;
                await markPaid(
                  { id: posting.id, fonte: "firestore" },
                  settlementDate,
                );
                // Gera contrapartida RECEITA se solicitado
                if (generateCounterpart && posting.group !== "RECEITAS") {
                  await savePosting(
                    {
                      group: "RECEITAS",
                      subCategory: "Retirada PJ Recta Scripta",
                      description: `Retirada PJ Recta Scripta → ${generateCounterpart.description}`,
                      account: posting.account || "C6 PJ (Pix)",
                      amount:
                        Math.round(Number(generateCounterpart.amount) * 100) /
                        100,
                      date: generateCounterpart.date || posting.date,
                      competenceDate: (
                        generateCounterpart.date || posting.date
                      )?.slice(0, 7),
                      isPaid: true,
                      statusType: "PAID",
                      needsSettlement: false,
                      settlementDate: settlementDate,
                      modoLancamento: "UNICO",
                      recorrencia: "Única",
                      repetirMeses: 0,
                      juros: 0,
                      desconto: 0,
                      acrescimo: 0,
                      tarifa: 0,
                    },
                    "SINGLE",
                  );
                }
              }
              setSettlementTarget(null);
            } catch (e) {
              alert("Erro: " + e.message);
            }
          }}
        />
      )}
    </div>
  );
};

const exportToCSV = (data, filename = "financeiro_pessoal.csv") => {
  if (!data || !data.length) return alert("Sem dados.");
  const headers = [
    "Data",
    "Descrição",
    "Grupo",
    "Categoria",
    "Conta",
    "Valor",
    "Status",
    "Tipo",
  ];
  const rows = data.map((r) => [
    r.date,
    `"${r.description}"`,
    r.group,
    r.category,
    r.account || "Geral",
    r.value,
    r.status,
    r.group === "RECEITAS" ? "ENTRADA" : "SAIDA",
  ]);
  const csv = [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
